import { getCookieValue } from '@/utils/cookies';
import type { RequestInterceptor } from '../../types';

/**
 * MockServer parallel-test worker isolation interceptor.
 *
 * When running mock-tests in parallel, each WDIO worker sets an
 * `x-mock-worker-id` cookie in the browser. This interceptor reads that
 * cookie and forwards it as a request header to the backend (MockServer),
 * so expectations can be scoped per worker.
 *
 * Uses getCookieValue which handles both server-side (Next.js cookies())
 * and client-side (document.cookie) automatically.
 *
 * Safety:
 * - Only adds the header when the cookie is present (never in production).
 * - The cookie name `x-mock-worker-id` is never set outside of test runs.
 */
const MOCK_WORKER_COOKIE = 'x-mock-worker-id';

export const mockWorkerInterceptor: RequestInterceptor = async (ctx, next) => {
    const workerId = await getCookieValue(MOCK_WORKER_COOKIE);

    // No cookie → no-op (production, non-parallel, or non-mock environments)
    if (!workerId) {
        return next(ctx);
    }

    return next({
        input: ctx.input,
        init: {
            ...ctx.init,
            headers: {
                ...ctx.init?.headers,
                [MOCK_WORKER_COOKIE]: workerId
            }
        },
        options: ctx.options
    });
};
